/**
 * Register the PWA service worker (production only).
 * Soft-fails on localhost HTTP and unsupported browsers.
 */

// How often to ask the browser to re-check sw.js during a long-lived session.
const UPDATE_POLL_MS = 30 * 60 * 1000;

/**
 * Reload once when a new service worker takes control.
 *
 * skipWaiting() + clients.claim() only transfer *control* of the page; the
 * document keeps running the hashed bundle it loaded at startup. Without this
 * the user sees stale UI until they manually hard-refresh.
 *
 * Guarded two ways: `reloading` stops the reload firing twice, and the caller
 * only arms this when a controller already existed, so a first-ever install
 * (which also fires controllerchange via clients.claim) does not reload.
 */
function reloadOnControllerChange() {
  let reloading = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloading) return;
    reloading = true;
    window.location.reload();
  });
}

export function registerServiceWorker() {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;

  const isLocalhost =
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname === '[::1]';

  // Allow opt-in on localhost via ?pwa=1 for testing.
  const forceLocal = new URLSearchParams(window.location.search).get('pwa') === '1';
  if (process.env.NODE_ENV !== 'production' && !forceLocal && isLocalhost) {
    return;
  }

  // Only arm the auto-reload if this page is already controlled. On a first
  // visit there is nothing stale to replace, so reloading would be pointless
  // churn (and on a slow connection, visible).
  if (navigator.serviceWorker.controller) {
    reloadOnControllerChange();
  }

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register(`${process.env.PUBLIC_URL || ''}/sw.js`)
      .then((registration) => {
        registration.onupdatefound = () => {
          const installing = registration.installing;
          if (!installing) return;
          installing.onstatechange = () => {
            if (installing.state === 'installed' && navigator.serviceWorker.controller) {
              // New version ready. Activating it fires controllerchange, which
              // reloads the page onto the new bundle.
              if (registration.waiting) {
                registration.waiting.postMessage({ type: 'SKIP_WAITING' });
              }
            }
          };
        };

        // Browsers only re-check sw.js on in-scope navigation. This is an SPA,
        // so client-side routing never triggers that and an installed PWA can
        // sit on an old build indefinitely. Poll instead.
        const checkForUpdate = () => {
          registration.update().catch(() => {});
        };

        checkForUpdate();
        setInterval(checkForUpdate, UPDATE_POLL_MS);

        // Returning to a backgrounded tab is the moment a stale shell is most
        // likely and most visible — check then too.
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') checkForUpdate();
        });
      })
      .catch((err) => {
        console.warn('[PWA] Service worker registration failed:', err);
      });
  });
}
